import { Component } from '@theme/component';
import { morph } from '@theme/morph';

/**
 * A custom element that handles the expansion of product details within the grid.
 */
class ProductExpander extends HTMLElement {
  static #cachedContent = new Map();
  #abortController = null;
  #activeProductId = null;

  constructor() {
    super();
  }

  connectedCallback() {
    if (!document.getElementById('product-expander-styles')) {
      const style = document.createElement('style');
      style.id = 'product-expander-styles';
      style.textContent = `
        .product-expander {
          grid-column: 1 / -1;
          width: 100.2%;
          margin-left: -0.1%;
          display: none;
          list-style: none;
          max-width: 1200px;
          margin: 0 auto;
        }
        .product-expander .product-details.sticky-content--desktop {
          position: relative !important;
          top: 0 !important;
          height: auto !important;
          overflow: visible !important;
        }
        .product-expander__close {
          transition: transform 0.2s ease, background-color 0.2s ease;
        }
        .product-expander__close:hover {
          transform: scale(1.1);
          background-color: rgba(0,0,0,0.05);
        }
        .product-expander__content {
          display: flex;
          flex-wrap: wrap;
          gap: 2rem;
          align-items: flex-start;
        }
        .product-expander__media-col {
          flex: 1 1 500px;
          min-width: 300px;
        }
        .product-expander__details-col {
          flex: 1 1 400px;
          min-width: 300px;
        }
        .product-expander__stockists {
          width: 100%;
        }
        .product-expander__progress {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 4px;
          background-color: rgba(0,0,0,0.05);
          overflow: hidden;
          z-index: 40;
        }
        .product-expander__progress-bar {
          height: 100%;
          width: 0;
          background-color: var(--color-foreground);
          transition: width 0.3s ease-out;
        }
        @media screen and (max-width: 749px) {
          .product-expander__content {
            gap: 2rem;
          }
        }
      `;
      document.head.appendChild(style);
    }

    document.addEventListener('product-card:expand', this.#boundHandleExpand);
    this.addEventListener('click', this.#handleCloseClick);
  }

  disconnectedCallback() {
    document.removeEventListener('product-card:expand', this.#boundHandleExpand);
    this.removeEventListener('click', this.#handleCloseClick);
  }

  /** @param {Event} event */
  #boundHandleExpand = (event) => {
    const customEvent = /** @type {CustomEvent} */ (event);
    this.handleExpandRequest(customEvent);
  };

  /** @param {Event} event */
  #handleCloseClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('.product-expander__close')) {
      this.collapse();
    }
  };

  /**
   * Handles the request to expand a product card.
   * @param {CustomEvent} event - The expand request event
   */
  async handleExpandRequest(event) {
    const { productId, url, card } = event.detail;

    // Toggle if clicking the same product
    if (this.#activeProductId === productId) {
      this.collapse();
      return;
    }

    this.#activeProductId = productId;
    await this.expand(card, url);
  }

  /**
   * Expands the details for a given card.
   * @param {HTMLElement} card - The product-card element
   * @param {string} url - The product URL to fetch
   */
  async expand(card, url) {
    const gridItem = card.closest('li.product-grid__item');
    if (!gridItem || !(gridItem instanceof HTMLElement)) {
      return;
    }

    // Ensure this expander only responds to cards in its own grid
    if (this.parentElement !== gridItem.parentElement) {
      return;
    }

    // 1. Find the last item in the visual row
    const lastItemInRow = this.#findLastItemInRow(gridItem);

    // 2. Clear and show loading with a slick transition
    this.innerHTML = `
      <div class="product-expander__inner" style="position: relative; background: var(--color-background); border-top: 1px solid var(--color-border); border-bottom: 1px solid var(--color-border); width: 100%;">
        <div class="product-expander__progress">
          <div class="product-expander__progress-bar" ref="progressBar"></div>
        </div>
        <div class="product-expander__loading" style="height: 150px; display: flex; align-items: center; justify-content: center;">
          <div class="loading-spinner"></div>
          <span style="margin-left: 1rem; font-size: 0.9rem; letter-spacing: 0.1em; text-transform: uppercase;">${this.getAttribute('loading-text') || 'Discovering Details...'}</span>
        </div>
      </div>
    `;
    this.style.display = 'block';

    const progressBar = this.querySelector('[ref="progressBar"]');
    if (progressBar instanceof HTMLElement) {
      // Small delay then start animation
      setTimeout(() => progressBar.style.width = '70%', 50);
    }

    // 3. Smoothly insert into row
    if (this.nextElementSibling !== lastItemInRow.nextElementSibling) {
      lastItemInRow.after(this);
    }

    // 4. Fetch content
    let data = ProductExpander.#cachedContent.get(url);
    if (!data) {
      data = await this.#fetchProductData(url);
      if (data) {
        ProductExpander.#cachedContent.set(url, data);
        if (progressBar instanceof HTMLElement) progressBar.style.width = '95%';
      }
    } else {
      if (progressBar instanceof HTMLElement) progressBar.style.width = '100%';
    }

    if (data) {
      const mediaNode = /** @type {Element} */ (data.media.cloneNode(true));
      const detailsNode = /** @type {Element} */ (data.details.cloneNode(true));

      // Inject the Slick container
      this.innerHTML = `
        <div class="product-expander__inner color-scheme-1 gradient" id="ProductInformation-expander"
             style="padding: clamp(2rem, 5vw, 4rem) 0; position: relative; background: var(--color-background); width: 100%;">
          <div class="page-width">
            <button class="product-expander__close" aria-label="Close"
                    style="position: absolute; top: 1.5rem; right: 1.5rem; z-index: 30; cursor: pointer; background: white; border: none; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
            <div class="product-expander__reveal-container" style="opacity: 0; transform: translateY(15px); transition: all 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);">
              <div class="product-expander__content">
                <div class="product-expander__media-col" ref="mediaCol"></div>
                <div class="product-expander__details-col" ref="detailsCol"></div>
                <div class="product-expander__stockists" ref="stockistsCol" style="display: none;"></div>
              </div>
            </div>
          </div>
        </div>
      `;

      const mediaTarget = this.querySelector('[ref="mediaCol"]');
      const detailsTarget = this.querySelector('[ref="detailsCol"]');
      const stockistsTarget = this.querySelector('[ref="stockistsCol"]');
      const revealContainer = this.querySelector('.product-expander__reveal-container');

      if (mediaTarget && detailsTarget && revealContainer instanceof HTMLElement) {
        morph(mediaTarget, mediaNode);
        morph(detailsTarget, detailsNode);

        if (data.stockists && stockistsTarget instanceof HTMLElement) {
          const stockistNode = /** @type {Element} */ (data.stockists.cloneNode(true));
          morph(stockistsTarget, stockistNode);

          // Force update for stockists to ensure they render initial barcode
          const stockistComponent = stockistsTarget.querySelector('product-stockists-component');
          if (stockistComponent && typeof stockistComponent.updateStockists === 'function') {
            stockistComponent.updateStockists();
          }
          stockistsTarget.style.display = 'block';
        }

        requestAnimationFrame(() => {
          revealContainer.style.opacity = '1';
          revealContainer.style.transform = 'translateY(0)';
        });

        setTimeout(() => {
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 200);
      }
    }
  }

  collapse() {
    this.style.display = 'none';
    this.#activeProductId = null;
  }

  /**
   * Finds the last grid item in the same row as the given element.
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #findLastItemInRow(item) {
    const parent = item.parentElement;
    if (!parent) return item;

    const items = Array.from(parent.querySelectorAll('li.product-grid__item'));
    const itemOffsetTop = item.offsetTop;
    const itemHeight = item.offsetHeight;

    let lastInRow = item;
    for (const other of items) {
      if (other instanceof HTMLElement) {
        if (Math.abs(other.offsetTop - itemOffsetTop) < itemHeight * 0.5) {
          lastInRow = other;
        } else if (other.offsetTop > itemOffsetTop) {
          break;
        }
      }
    }
    return lastInRow;
  }

  /**
   * Fetches the product data.
   * @param {string} url
   */
  async #fetchProductData(url) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#abortController.signal });
      if (!response.ok) {
        return null;
      }
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      const media = html.querySelector('.product-information__media');
      const details = html.querySelector('.product-details');
      const stockists = html.querySelector('.section-product-stockists, product-stockists-component')?.closest('section');

      if (!media || !details) return null;

      return {
        media: media.cloneNode(true),
        details: details.cloneNode(true),
        stockists: stockists ? stockists.cloneNode(true) : null
      };
    } catch (error) {
      return null;
    }
  }
}

if (!customElements.get('product-expander')) {
  customElements.define('product-expander', ProductExpander);
}
